import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Autocomplete, TextField, Chip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDashboard } from '../context/DashboardProvider';
import { useTheme } from '@mui/material/styles';

const PageSelector = () => {
  const { selectedArticles, setSelectedArticles } = useDashboard();
  const theme = useTheme();
  const [wikiPages, setWikiPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStates, setSelectedStates] = useState(['Michigan']); // ✅ Default state = Michigan

  const DEFAULT_AVAS = ['Fennville_AVA', 'Leelanau_Peninsula_AVA']; // ✅ Default AVAs

  useEffect(() => {
    fetch('/articles.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load articles.json');
        }
        return response.json();
      })
      .then((data) => {
        setWikiPages(data);
        setLoading(false);

        if (selectedArticles.length === 0) {
          // Ensure both default AVAs exist in the data
          const defaultAvasInData = data
            .filter((p) => DEFAULT_AVAS.includes(p.article))
            .map((p) => p.article);

          if (defaultAvasInData.length > 0) {
            setSelectedArticles(defaultAvasInData);
          }
        }
      })
      .catch((error) => {
        console.error('Error loading articles:', error);
        setLoading(false);
      });
  }, []);

  const normalizeText = (text) => (text ? text.toLowerCase().replace(/\s+/g, '') : '');

  // Extract unique states, splitting multi-state entries correctly
  const allStates = [...new Set(wikiPages.flatMap((p) => p.state?.split(', ') || []))].sort();

  const filteredPages = wikiPages.filter((p) =>
    selectedStates.length === 0 || selectedStates.some((state) => p.state?.split(', ').includes(state))
  );

  const handleSelectArticle = (event, newValue) => {
    setSelectedArticles(newValue.map((option) => option.article));
  };

  const handleRemoveArticle = (article) => {
    setSelectedArticles(selectedArticles.filter((a) => a !== article));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: theme.typography.datePicker }}>
        Select State(s)
      </Typography>

      <Autocomplete
        multiple
        options={allStates}
        value={selectedStates}
        onChange={(event, newValue) => setSelectedStates(newValue)}
        filterOptions={(options, { inputValue }) =>
          options.filter((option) => normalizeText(option).includes(normalizeText(inputValue)))
        }
        renderInput={(params) => <TextField {...params} variant="outlined" />}
        sx={{ mb: 2 }}
      />

      <Typography variant="h6" gutterBottom sx={{ fontSize: theme.typography.datePicker }}>
        Select Pages
      </Typography>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Autocomplete
          multiple
          options={filteredPages}
          getOptionLabel={(option) => option.label}
          value={selectedArticles
            .map((article) => filteredPages.find((p) => p.article === article))
            .filter(Boolean)}
          onChange={handleSelectArticle}
          filterOptions={(options, { inputValue }) =>
            options.filter((option) => normalizeText(option.label).includes(normalizeText(inputValue)))
          }
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => {
              const articleData = wikiPages.find((p) => p.article === option.article);
              return (
                <Chip
                  key={option.article}
                  label={
                    <span
                      onClick={() => window.open(articleData.link, '_blank', 'noopener,noreferrer')}
                      style={{ cursor: 'pointer' }}
                    >
                      {articleData.label}
                    </span>
                  }
                  {...getTagProps({ index })}
                  deleteIcon={
                    <IconButton
                      size="small"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => handleRemoveArticle(option.article)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{
                    fontSize: 12,
                    px: 0,
                    py: 0.5,
                  }}
                />
              );
            })
          }
          renderInput={(params) => <TextField {...params} variant="outlined" />}
          sx={{ mb: 2 }}
        />
      )}
    </Box>
  );
};

export default PageSelector;
